import type { Breadcrumb, ErrorEvent } from "@sentry/react"

/**
 * Keep URL fragments out of error reports.
 *
 * A recipient's signing link carries its token in the fragment, precisely so it
 * never reaches a server. Sentry's browser SDK is the exception: it reads
 * `location.href` for every event and keeps navigation breadcrumbs, both of
 * which would carry that token out of the browser — and `sendDefaultPii` is on,
 * so nothing else is holding it back.
 *
 * Fragments are dropped everywhere rather than only on the signing page. No
 * page in this app puts anything in one that is worth reporting, and a rule with
 * an exception is a rule somebody will later get wrong.
 */
const withoutFragment = (value: string): string => {
  const hash = value.indexOf("#")
  return hash === -1 ? value : `${value.slice(0, hash)}#redacted`
}

const scrubField = (holder: object | undefined, key: string): void => {
  if (!holder) return
  const record = holder as Record<string, unknown>
  const value = record[key]
  if (typeof value === "string" && value.includes("#")) {
    record[key] = withoutFragment(value)
  }
}

const scrubCrumbData = (crumb: Breadcrumb): void => {
  scrubField(crumb.data, "from")
  scrubField(crumb.data, "to")
  scrubField(crumb.data, "url")
}

export const scrubUrlFragments = (event: ErrorEvent): ErrorEvent => {
  scrubField(event.request, "url")
  scrubField(event, "culprit")
  event.breadcrumbs?.forEach(scrubCrumbData)
  return event
}

export const scrubBreadcrumbFragments = (crumb: Breadcrumb): Breadcrumb => {
  scrubCrumbData(crumb)
  return crumb
}
